//@ts-ignore
import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import AudioSubtitle from '../AudioSubtitle';
import { Sounds } from './styled';

import { TAudioPlayerProps } from './types';
import { FAIcon } from '@packages/ui-components';

const INIT_SETTINGS = {
  playing: false,
  played: 0,
  hasSubtitles: false,
  showSubtitles: true,
};

type Keys = keyof typeof INIT_SETTINGS;
type Values = (typeof INIT_SETTINGS)[Keys];

function formatDuration(value: number) {
  const minute = Math.floor(value / 60);
  const secondLeft = value - minute * 60;
  return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`;
}

export function AudioPlayer({
  url,
  subtitles,
  subtitlesSettings,
  autoplay = false,
}: TAudioPlayerProps) {
  const [caption, setCaption] = useState<string>();
  const [player, setPlayer] = useState(INIT_SETTINGS);

  const audioRef = React.useRef<HTMLAudioElement>(null);

  const set = (key: Keys, value: Values) => {
    setPlayer((prev) => ({ ...prev, [key]: value }));
  };

  const handlePlay = useCallback(() => {
    set('playing', true);
  }, []);

  const handlePause = useCallback(() => {
    set('playing', false);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    set('played', Math.floor(audioRef?.current?.currentTime || 0));
  }, []);

  useEffect(() => {
    const element = audioRef?.current;

    if (!element) {
      return;
    }

    element.addEventListener('play', handlePlay);
    element.addEventListener('pause', handlePause);
    element.addEventListener('timeupdate', handleTimeUpdate);

    if (subtitles?.length) {
      set('hasSubtitles', true);
      if (audioRef?.current) {
        const textTrack1 = audioRef?.current?.addTextTrack('captions');
        if (textTrack1) {
          textTrack1.mode = 'showing';
          subtitles.forEach((musicSub: { from: number; to: number; text: string }) =>
            textTrack1.addCue(new VTTCue(musicSub.from / 1000, musicSub.to / 1000, musicSub.text)),
          );
          textTrack1.oncuechange = () => {
            //@ts-ignore
            const currentCue = audioRef?.current?.textTracks?.[0]?.activeCues?.[0]?.text;

            if (currentCue) {
              setCaption(currentCue);
            }
          };
        }
      }
    }

    return () => {
      element.removeEventListener('play', handlePlay);
      element.removeEventListener('pause', handlePause);
      element.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [handlePause, handlePlay, handleTimeUpdate, subtitles]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    if (autoplay) {
      audioRef?.current?.play();
    }
  }, [autoplay]);

  return (
    <>
      <Sounds>
        {caption && (
          <AudioSubtitle
            caption={caption}
            isShown={player.showSubtitles}
            settings={subtitlesSettings}
          />
        )}
        <div className="sound-range">
          <input
            type="range"
            min={0}
            max={audioRef?.current?.duration}
            step={1}
            value={player.played}
            onChange={(event) => {
              if (audioRef?.current?.currentTime) {
                audioRef.current.currentTime = Number(event.target.value);
              }
            }}
          />
        </div>
        <div className="sound-controls">
          <div className="left-side">
            <div className="play">
              <FAIcon
                icon={player.playing ? 'faPause' : 'faPlay'}
                size={16}
                onClick={() =>
                  player.playing ? audioRef?.current?.pause() : audioRef?.current?.play()
                }
              />
            </div>
            <div className="volume">
              <FAIcon icon="faVolumeUp" size={16} />

              <input
                type="range"
                step="0.1"
                min="0"
                max="1"
                value={audioRef?.current?.volume}
                onChange={(event) => {
                  if (audioRef?.current?.volume) {
                    audioRef.current.volume = Number(event.target.value);
                  }
                }}
              />
            </div>
            {player.hasSubtitles && (
              <FAIcon
                icon="faClosedCaptioning"
                size={16}
                className={`caption ${player.showSubtitles ? 'active' : ''}`}
                onClick={() => set('showSubtitles', !player.showSubtitles)}
              />
            )}

            {player.played > 0 && <span className="time">{formatDuration(player.played)}</span>}
          </div>
        </div>
      </Sounds>

      <audio ref={audioRef} controls hidden>
        <source src={url} type="audio/mpeg" />
      </audio>
    </>
  );
}
